import Vue from 'vue';
import VueRouter, { RouteConfig, NavigationGuardNext, Route } from 'vue-router';
import store from '@/store/index';
import ClientView from '@/views/Client.vue';

Vue.use(VueRouter);

const handleUnsavedData = (): boolean => {
    if (store.state.unsavedData) {
        store.commit('updateShowUnSavedMessage', true);
        return true;
    }

    return false;
};

const commonRouteGuard = (to: Route, from: Route, next: NavigationGuardNext) => {
    if (handleUnsavedData()) return;
    next();
};

const routes: Array<RouteConfig> = [
    {
        path: '/',
        name: 'Client',
        component: ClientView,
        beforeEnter: commonRouteGuard,
    },
    {
        path: '/manager-settings',
        name: 'ManagerSettings',
        beforeEnter: commonRouteGuard,
        component: () => import('../views/ManagerSettings.vue'),
    },
    {
        path: '/strategic-allocations',
        name: 'StrategicAllocations',
        beforeEnter: commonRouteGuard,
        component: () => import('../views/StrategicAllocations.vue'),
    },
    {
        path: '/market-value-cashflow',
        name: 'MarketValueCashflow',
        beforeEnter: commonRouteGuard,
        component: () => import('../views/MarketValueAndCashflow.vue'),
    },
    {
        path: '/exposure-values',
        name: 'ExposureValues',
        beforeEnter: (to, from, next) => {
            if (handleUnsavedData()) return;

            const availableTypes = store.state.availableInvestmentTypes;
            if (availableTypes && !availableTypes.hasExposureInvestments) {
                next('/market-value-cashflow');
                return;
            }

            if (!availableTypes) {
                store.dispatch('getAvailableInvestmentTypes').then(() => {
                    const types = store.state.availableInvestmentTypes;
                    if (types && !types.hasExposureInvestments) {
                        next('/market-value-cashflow');
                    } else {
                        next();
                    }
                });
                return;
            }

            next();
        },
        component: () => import('../views/MarketValueAndCashflow.vue'),
    },
    {
        path: '/additional-metrics',
        name: 'AdditionalMetrics',
        beforeEnter: (to, from, next) => {
            if (handleUnsavedData()) return;

            const availableTypes = store.state.availableInvestmentTypes;
            if (availableTypes && !availableTypes.hasAdditionalMetrics) {
                next('/market-value-cashflow');
                return;
            }

            if (!availableTypes) {
                store.dispatch('getAvailableInvestmentTypes').then(() => {
                    const types = store.state.availableInvestmentTypes;
                    if (types && !types.hasAdditionalMetrics) {
                        next('/market-value-cashflow');
                    } else {
                        next();
                    }
                });
                return;
            }

            next();
        },
        component: () => import('../views/MarketValueAndCashflow.vue'),
    },
    {
        path: '/investment-performance-overrides',
        name: 'InvestmentPerformanceOverrides',
        props: { performanceType: 'managerPerformance' },
        beforeEnter: commonRouteGuard,
        component: () => import('../views/Performance.vue'),
    },
    {
        path: '/benchmark-performance-overrides',
        name: 'BenchmarkPerformanceOverrides',
        props: { performanceType: 'benchmarkPerformance' },
        beforeEnter: commonRouteGuard,
        component: () => import('../views/Performance.vue'),
    },
    {
        path: '/bulk-operations',
        name: 'BulkOperations',
        beforeEnter: commonRouteGuard,
        component: () => import('../views/BulkOperations.vue'),
    },
    {
        path: '/health-check',
        name: 'HealthCheck',
        beforeEnter: commonRouteGuard,
        component: () => import('../views/HealthCheck.vue'),
    },
    {
        path: '/alternative-calculations-settings',
        name: 'AlternativeCalculationsSettings',
        beforeEnter: commonRouteGuard,
        component: () => import('../views/AlternativeCalculationsSettingsView.vue'),
    },
    {
        path: '/client-setup',
        name: 'ClientSetup',
        beforeEnter: commonRouteGuard,
        component: () => import('../views/ClientSetupView.vue'),
    },
    {
        path: '/release-audit',
        name: 'ReleaseAudit',
        beforeEnter: commonRouteGuard,
        component: () => import('../views/ReleaseAudit.vue'),
    },
];

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
});

export default router;
